import { MouseEvent, ReactElement, ReactNode } from 'react'
import { Box, Stack, ToggleButton, Tooltip, Typography } from '@mui/material'
import { FilterIconButton, SelectIconButton, SettingsIconButton } from '@ui/actions'

const wrapSettingsTooltip = (button: ReactNode, tooltip?: string): ReactNode => {
  if (!tooltip) return button
  return (
    <Tooltip title={tooltip}>
      <span>{button}</span>
    </Tooltip>
  )
}

interface StoreyTypeToggleButtonProps {
  value: string
  label: string
  selected: boolean
  onToggle: () => void
  /** Receives the click event so callers can extend the selection on shift-click. */
  onSelect?: (event: MouseEvent<HTMLButtonElement>) => void
  onClickSettings?: (event: MouseEvent<HTMLButtonElement>) => void
  dataCy?: string
  settingsDataCy?: string
  /** Glyph for the `onClickSettings` button — a gear (default) or a filter icon. */
  settingsIcon?: 'settings' | 'filter'
  /** Hover text for the settings or filter icon. Omitted buttons stay without a tooltip. */
  settingsTooltip?: string
  /** Optional color swatch shown before the label (e.g. the IFC element-type color). */
  color?: string
}

const StoreyTypeToggleButton = ({
  value,
  label,
  selected,
  onToggle,
  onSelect,
  onClickSettings,
  dataCy,
  settingsDataCy,
  settingsIcon = 'settings',
  settingsTooltip,
  color,
}: StoreyTypeToggleButtonProps): ReactElement => {
  const handleSelectClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    onSelect?.(event)
  }

  const handleSettingsClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    onClickSettings?.(event)
  }

  return (
    <Stack direction="row" alignItems="center" spacing={0.25} padding={0.25}>
      <ToggleButton
        size="small"
        value={value}
        selected={selected}
        onClick={onToggle}
        sx={{ padding: 0, minWidth: 'auto', border: 'none', px: 0.5 }}
        data-cy={dataCy}
      >
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {color && (
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '2px',
                border: '1px solid',
                borderColor: 'grey.400',
                bgcolor: color,
                flexShrink: 0,
              }}
            />
          )}
          <Typography fontSize="small" noWrap>
            {label}
          </Typography>
        </Stack>
      </ToggleButton>
      {onSelect && (
        <SelectIconButton
          size="small"
          onClick={handleSelectClick}
          sx={{ padding: 0, width: 18, height: 18 }}
        />
      )}
      {onClickSettings &&
        wrapSettingsTooltip(
          settingsIcon === 'filter' ? (
            <FilterIconButton
              size="small"
              aria-label={settingsTooltip}
              onClick={handleSettingsClick}
              sx={{ padding: 0, width: 18, height: 18 }}
              data-cy={settingsDataCy}
            />
          ) : (
            <SettingsIconButton
              size="small"
              aria-label={settingsTooltip}
              onClick={handleSettingsClick}
              sx={{ padding: 0, width: 18, height: 18 }}
              data-cy={settingsDataCy}
            />
          ),
          settingsTooltip,
        )}
    </Stack>
  )
}

export default StoreyTypeToggleButton
